import { Component, Inject, OnInit, Optional } from '@angular/core';
import { EbwModalService } from '@ebw-monorepo/modal';
import { HOME_SPLIT_VIEW_SERVICE_ADAPTER } from '@ebw-monorepo/utils';
import { TranslateService } from '@ngx-translate/core';
import { CustomerEnum } from 'libs/ng-components/utils/src/lib/enums/customer.enum';
import { Feature } from 'ol';
import { FeatureNode, NODE_TYPES } from '../../../models/feature-note';
import { FeatureSelectService } from '../../../services/feature-select.service';
import { FeatureViewerService } from '../../../services/feature-viewer.service';
import { MapService } from '../../../services/map.service';
import { PsaService } from '../../../services/psa.service';
import { REPORT_DIALOG_CUSTOM_STYLE, ReportDialogStyleConfig } from '../../report-dialog/report-dialog.component';
import { SnackbarService } from '../../../services/snackbar.service';
import { parseUrn } from '../../../utils/urn.utils';

interface PredictionItem {
  label: string;
  value: any;
  tooltip: string;
}

interface PredictionSection {
  group: string;
  items: PredictionItem[];
}

interface AreaInfoTabs {
  prediction: PredictionSection[];
  quotations: any[];
}

@Component({
  selector: 'ebw-monorepo-feature-viewer-details',
  templateUrl: './feature-viewer-details.component.html',
  styleUrls: ['./feature-viewer-details.component.scss']
})
export class FeatureViewerDetailsComponent implements OnInit {
  public features: Feature[] = [];
  public currentNode: FeatureNode;
  public featureDetailStack: { title: string; data: any; properties?: any; tabs?: AreaInfoTabs }[] = [];
  private _featureMaxDetail = 3;
  public customerEnum = CustomerEnum;
  public readonly customReportStyle: ReportDialogStyleConfig = REPORT_DIALOG_CUSTOM_STYLE;

  private _quotationMarkers: Map<number, Feature<any>> = new Map();
  private _expandedPredictions: Map<number, boolean> = new Map();
  private _expandedQuotations: Map<number, boolean> = new Map();

  constructor(
    @Optional() @Inject(HOME_SPLIT_VIEW_SERVICE_ADAPTER) public componentData: any,
    private _ebwModal: EbwModalService,
    private _featureViewerService: FeatureViewerService,
    private _featureSelectService: FeatureSelectService,
    private _mapService: MapService,
    private _translateService: TranslateService,
    private _psaService: PsaService,
    private _snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    if (this.componentData) {
      this.features = this.componentData;
    }

    this._featureViewerService.features$.subscribe((features) => {
      this.featureDetailStack = [];
      if (!features) {
        this.features = [];
        return;
      }
      this.features = features;
    });
  }

  public removeDetail(index) {
    this.featureDetailStack.splice(index, 1);
  }

  public onNodeSelection(node: FeatureNode) {
    switch (node.type) {
      case NODE_TYPES.DATASET:
        break;
      case NODE_TYPES.COLLECTION:
        break;
      case NODE_TYPES.FEATURE:
        this.showDetail(node.nodeData);
        this.onNodeSecondaryAction({ node, action: 'zoom-fit' });
        break;
    }
  }

  public onNodeSecondaryAction(event: { node: FeatureNode; action: string }) {
    switch (event.node.type) {
      case NODE_TYPES.DATASET:
        switch (event.action) {
          case 'zoom-fit':
          case 'highlight':
            let features = [];
            event.node.children.forEach((_) => {
              features = features.concat(_.children.map((f) => f.nodeData));
            });

            this._featureViewerService.notifyNodeAction({ indexChange: -1, action: event.action, features });
            break;
        }
        break;
      case NODE_TYPES.COLLECTION:
        switch (event.action) {
          case 'zoom-fit':
          case 'highlight':
            this._featureViewerService.notifyNodeAction({
              indexChange: -1,
              action: event.action,
              features: event.node.children.map((_) => _.nodeData)
            });
            break;
        }
        break;
      default:
        this._featureViewerService.notifyNodeAction({
          indexChange: 0,
          action: event.action,
          features: [event.node.nodeData]
        });
    }
  }

  private async showDetail(feature: Feature) {
    this._mapService.removeAllMarkerFromTemporaryLayer();
    this._quotationMarkers.clear();
    this._expandedPredictions.clear();
    this._expandedQuotations.clear();

    const data = await this._getFeatureDataToDisplay(feature);
    if (data) {
      this.featureDetailStack = [{ title: data.title, data: data.data, properties: feature.getProperties(), tabs: data.tabs }];
      if (data.tabs) {
        this._addQuotationMarkers(data.tabs.quotations);
      }
    }
  }

  private _addQuotationMarkers(quotations: any[]): void {
    quotations.forEach((q, i) => {
      if (q['Latitude'] == null || q['Longitude'] == null) return;
      const lng = parseFloat(String(q['Longitude']).replace(',', '.'));
      const lat = parseFloat(String(q['Latitude']).replace(',', '.'));
      const feature = this._mapService.addMarkerToTemporaryLayer(`quotation-${i}`, [lng, lat], MapService.EPSG_4326_KEY, 'QuotationStyle');
      if (feature) this._quotationMarkers.set(i, feature);
    });
  }

  centerOnQuotation(index: number): void {
    const feature = this._quotationMarkers.get(index);
    if (!feature) return;
    this._mapService.centerMapOnGeometry(feature.getGeometry(), true);
  }

  onQuotationHover(index: number, q: any): void {
    if (q['Latitude'] == null || q['Longitude'] == null) return;
    const title = `quotation-${index}`;
    const lng = parseFloat(String(q['Longitude']).replace(',', '.'));
    const lat = parseFloat(String(q['Latitude']).replace(',', '.'));
    this._mapService.removeMarkerFromTemporaryLayer(title);
    const feature = this._mapService.addMarkerToTemporaryLayer(title, [lng, lat], MapService.EPSG_4326_KEY, 'QuotationHighlightStyle');
    if (feature) this._quotationMarkers.set(index, feature);
  }

  onQuotationLeave(index: number, q: any): void {
    if (q['Latitude'] == null || q['Longitude'] == null) return;
    const title = `quotation-${index}`;
    const lng = parseFloat(String(q['Longitude']).replace(',', '.'));
    const lat = parseFloat(String(q['Latitude']).replace(',', '.'));
    this._mapService.removeMarkerFromTemporaryLayer(title);
    const feature = this._mapService.addMarkerToTemporaryLayer(title, [lng, lat], MapService.EPSG_4326_KEY, 'QuotationStyle');
    if (feature) this._quotationMarkers.set(index, feature);
  }

  togglePrediction(index: number): void {
    this._expandedPredictions.set(index, !this._expandedPredictions.get(index));
  }

  isPredictionExpanded(index: number): boolean {
    return !!this._expandedPredictions.get(index);
  }

  toggleQuotation(index: number): void {
    this._expandedQuotations.set(index, !this._expandedQuotations.get(index));
  }

  isQuotationExpanded(index: number): boolean {
    return !!this._expandedQuotations.get(index);
  }

  objectEntries(obj: any): [string, any][] {
    return obj ? Object.entries(obj) : [];
  }

  isNumber(value: any) {
    return typeof value === 'number';
  }

  copyPredictionContent(section: PredictionSection): void {
    const text = section.items
      .filter(({ value }) => value !== null && value !== undefined)
      .map(({ label, value }) => `${label}: ${value}`)
      .join('\n');
    navigator.clipboard.writeText(text);
    this._snackbarService.open('Informazioni copiate');
  }

  copyQuotationContent(q: any): void {
    const text = Object.entries(q)
      .filter(([, v]) => v !== null && v !== undefined)
      .map(([k, v]) => `${k}: ${v}`)
      .join('\n');
    navigator.clipboard.writeText(text);
    this._snackbarService.open('Informazioni copiate');
  }

  public async addDetailFromFeature(feature) {
    if (!feature) {
      return;
    }
    await this._featureSelectService.mapFeatureToModal(feature);

    const mappedFeaure = this._featureSelectService.parseFeature(feature.get('details'));
    const data = { title: feature.get('details')?.title, data: this._featureSelectService.objectToArray(mappedFeaure) };

    if (this.featureDetailStack.length == this._featureMaxDetail) {
      //TODO: testo scelto a casa, ripensarci
      const confirmModal = this._ebwModal.openConfirmModal({
        title: 'Limite visualizzazione dettagli raggiunto',
        content: 'Per poter visualizzare questo dettaglio verrà eliminato il dettaglio più datato. Continuare ?'
      });

      confirmModal.afterClosed().subscribe((result) => {
        if (result == true) {
          this.removeDetail(0);
          this.featureDetailStack.push(data);
        }
      });
    } else {
      this.featureDetailStack.push(data);
    }
  }

  private async _getFeatureDataToDisplay(feature: Feature): Promise<{ title: string; data: any[]; tabs?: AreaInfoTabs } | null> {
    const urn = feature.getId().toString();

    // PATH 1 — has detailUrl: fetch from API then map properties
    if (feature.get('detailUrl')) {
      try {
        const response: any = await this._featureSelectService.getFeatureInfo(feature);

        // PATH 1b — area info: returns tabs instead of flat properties
        if (response.type === 'areaInfo') {
          return {
            title: 'Dettaglio Area ' + (feature.get('id') ?? urn),
            data: [],
            tabs: { prediction: response.prediction, quotations: response.quotations }
          };
        }

        feature.setProperties(response.properties);
        await this._featureSelectService.mapFeatureToModal(feature);
        const details = feature.get('details');
        if (!details) {
          return null;
        }
        return this._toDisplayData(details);
      } catch (_err) {
        this._ebwModal.openErrorModal({
          content: this._translateService.instant('common.data_retrieving_error'),
          title: this._translateService.instant('common.error')
        });
        return null;
      }
    }

    // PATH 2 — no detailUrl (shape, PSA, or feature without detail endpoint):
    // show raw properties with config metadata
    const props = feature.getProperties();
    if (!props || Object.keys(props).length === 0) {
      return null;
    }
    await this._featureSelectService.mapFeatureToModal(feature);
    const details = feature.get('details');
    return this._toDisplayData(details);
  }

  private _toDisplayData(source: any): { title: string; data: any[] } {
    const mapped = this._featureSelectService.parseFeature(source);
    return {
      title: source?.title,
      data: this._featureSelectService.objectToArray(mapped)
    };
  }

  isPsaObject(data: any) {
    const urnProp = data.find((prop) => prop?.key.toLowerCase() === 'urn');
    const urn = urnProp?.value;
    return this._psaService.isPsaObject(urn);
  }

  /**
   * Function to figure out if the structure tab should be visible or not
   *
   * @param detail
   * @returns
   */
  public shouldShowStructure(detail: { properties?: Record<string, string> }): boolean {
    if (detail.properties === undefined) {
      return true;
    }

    if (!detail.properties.urn) {
      return true;
    }

    const { dataset, collection, id } = parseUrn(detail.properties.urn);

    // Caso specifico per fastweb. I livelli aree_sgu, comuni, province e regioni non devono avere la struttura nel dettaglio
    //
    // Capire se è possibile generalizzare questo controllo e renderlo configurabile per cliente / livello
    //
    // Capire se vale la pena invertire il valore di default della visualizzazione delle strutture nel ReportDialogComponent
    // (al momento di default a true)
    if (['limiti', 'geo4business'].includes(dataset) && ['aree_sgu', 'comuni', 'province', 'regioni'].includes(collection)) {
      return false;
    }

    return true;
  }
}
